import { React, useState, useEffect, useMemo } from "react";
import { Typography, Box } from "@mui/material";
import RDataGrid from "../reusables/RDataGrid";
import PageLoader from "../loader/PageLoader";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import AddPopup from "../popup/AddPopup";
import Notification from "../popup/Notification";
import ConfirmDialog from "../dialog/ConfirmDialog";
import AddSupplier from "../forms/AddSupplier";

const Jobs = () => {
  const [isFetchingSuppliers, setIsFetchingSuppliers] = useState(true);
  const [editSupplier, setEditSupplier] = useState(false);
  const [viewSupplier, setViewSupplier] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [refreshSuppliers, setSetRefreshSuppliers] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const [suppliers, setSuppliers] = useState([]);
  const JOBS_URL = "api/v1/jobs/paged-by-tenant/";
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  const divStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const onDelete = async (id) => {
    const controller = new AbortController();
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    try {
      const response = await axiosPrivate.delete(JOBS_URL + "/" + id, {
        signal: controller.signal,
      });
      var message = response.data.message;
      setNotify({
        isOpen: true,
        message: message,
        type: "success",
      });
      setSetRefreshSuppliers(new Date());
    } catch (error) {
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      setConfirmDialog({
        isOpen: true,
        title: "Failed To Delete Supplier",
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: () => {
          onDelete(id);
        },
      });
    }
  };

  useEffect(() => {
    if (!isFetchingSuppliers) {
      setIsFetchingSuppliers(true);
    }

    let isMounted = true;
    const controller = new AbortController();

    const getJobs = async () => {
      try {
        const response = await axiosPrivate.get(
          `${JOBS_URL}${auth.tenantId}/0/10`,
          {
            signal: controller.signal,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        isMounted && setSuppliers(response.data.data.jobs);
        setIsFetchingSuppliers(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setIsFetchingSuppliers(false);
      }
    };

    getJobs();

    return () => {
      isMounted = false;
      controller.abort();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [refreshSuppliers]);

  const columns = useMemo(
    () => [
      {
        field: "rowNo", // from DataGrid rows defifnition
        headerName: "No.",
        sortable: false,
        headerClassName: "super-app-theme--header",
        minWidth: 87,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "customer",
        headerName: "Customer",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
        renderCell: function (params) {
          const customerName = params.row.customer.name;
          return customerName;
        },
      },
      {
        field: "speciality",
        headerName: "Speciality",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
        renderCell: function (params) {
          const speciality = params.row.speciality.name;
          return speciality;
        },
      },
      {
        field: "amount",
        headerName: "Amount",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
        renderCell: function (params) {
          return `Ksh.${params.row.amount}`;
        },
      },
      {
        field: "commission",
        headerName: "Commission",
        headerClassName: "super-app-theme--header",
        minWidth: 160,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
        renderCell: function (params) {
          const commission = 0.2 * params.row.amount;
          return `Ksh.${commission}`;
        },
      },
      {
        field: "status",
        headerName: "Status",
        type: "boolean",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
        renderCell: function (params) {
          const status = params.row.status.name;
          return status;
        },
      },
      {
        field: "worker",
        headerName: "Worker",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
        renderCell: function (params) {
          const name =
            params.row.worker.firstName + " " + params.row.worker.lastName;
          return name;
        },
      },
      {
        field: "timeStamp",
        headerName: "Date Created",
        headerClassName: "super-app-theme--header",
        minWidth: 200,
        flex: 1,
        //align: "center",
        //headerAlign: "center",
        align: "left",
        headerAlign: "left",
        editable: false,
        /*  renderCell: function (params) {
          const timestamp = params.row.timeStamp;
          var date = moment(timestamp).format(date_format2);
          var time = moment(timestamp).format(time_format);
          return `${date} at ${time}`;
        }, */
      },
    ],
    []
  );

  // Close dialog upon successful supplier add/update
  const closeDialog = (message) => {
    setOpenPopup(false);
    setNotify({
      isOpen: true,
      message: message,
      type: "success",
    });
    setSetRefreshSuppliers(new Date());
  };

  return isFetchingSuppliers ? (
    <PageLoader title="Loading suppliers..." />
  ) : (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      {suppliers?.length ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
          }}
        >
          <div style={divStyle}>
            <Typography
              sx={{
                textAlign: "left",
                mt: 3,
                mb: 3,
                fontSize: {
                  lg: 30,
                  md: 20,
                  sm: 20,
                  xs: 19,
                },
              }}
            >
              Jobs
            </Typography>
          </div>
          <Box
            sx={{
              width: "100%",
              margin: "0 auto 16px",
              height: 560,
              overflow: "hidden",
            }}
          >
            <RDataGrid columns={columns} rows={suppliers} rowHeight={63} />
          </Box>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={
              viewSupplier
                ? "View (Edit) Job"
                : editSupplier
                ? "Edit (View) Job"
                : "Add Job"
            }
          >
            <AddSupplier
              closeDialog={closeDialog}
              editSupplier={editSupplier}
              selectedSupplier={selectedSupplier}
            />
          </AddPopup>
        </Box>
      ) : (
        <div style={divStyle}>
          <Typography
            variant="h4"
            component="h4"
            sx={{ textAlign: "center", mt: 3, mb: 3 }}
          >
            No jobs to display
          </Typography>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={
              viewSupplier
                ? "View (Edit) Job"
                : editSupplier
                ? "Edit (View) Job"
                : "Add Job"
            }
          >
            <AddSupplier
              closeDialog={closeDialog}
              editSupplier={editSupplier}
              selectedSupplier={selectedSupplier}
            />
          </AddPopup>
        </div>
      )}
    </Box>
  );
};

export default Jobs;
